// ---------------------------------------------------------------------------
// Device capability detection — ES5-safe, no imports, runs before heavy code.
//
// Old Android TV / projector WebViews (Android 9 and older) cannot parse the
// modern JS that the Clerk SDK ships, so the app must NEVER load Clerk there.
// AuthContext checks isLegacyDevice() and boots in local mode on such devices.
// ---------------------------------------------------------------------------

// Android major version from the UA, or null when not Android.
export const getAndroidVersion = () => {
  try {
    const m = (navigator.userAgent || '').match(/Android\s+(\d+)\./i);
    return m ? parseInt(m[1], 10) : null;
  } catch (e) {
    return null;
  }
};

// True when the WebView is too old for the Clerk SDK (WebView Chrome < 80).
export const isLegacyDevice = () => {
  try {
    const ua = navigator.userAgent || '';

    // Desktop Chrome (non-mobile UA) is always modern enough.
    if (/Chrome\/(\d+)\./.test(ua) && !/Android|Mobile/i.test(ua)) {
      const chrome = parseInt((ua.match(/Chrome\/(\d+)\./) || [])[1] || '0', 10);
      return chrome < 80;
    }

    // Android WebView / TV: Android 9 and below -> legacy.
    const av = getAndroidVersion();
    if (av !== null) return av <= 9;

    // Non-Android UA with an explicit Chrome/Chromium version.
    const chromeM = ua.match(/Chrom(?:e|ium)\/(\d+)\./);
    if (chromeM) return parseInt(chromeM[1], 10) < 80;

    return false;
  } catch (e) {
    return false;
  }
};

// True when the app runs inside the Android TV wrapper (userAgent suffix set
// by MainActivity). On such runtimes we always show the pairing screen —
// even on modern Android versions — because there is no touch UI there.
export const isTvRuntime = () => {
  try {
    return / FlexWatchTV\/?/i.test(navigator.userAgent || '');
  } catch (e) {
    return false;
  }
};

// Wide viewport = desktop/TV. Mobile apps send them to the phone remote,
// wide screens open the standalone TV pairing page instead.
export const isWideViewport = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia) return window.matchMedia('(min-width: 768px)').matches;
    return (window.innerWidth || 0) >= 768;
  } catch (e) {
    return false;
  }
};

export const getTvRemotePath = () =>
  isWideViewport() ? '/tv' : '/remote';

// 6-digit pairing code (not cryptographically strong — fine for cast rooms).
export const generatePairCode = () => {
  try {
    const buf = new Uint32Array(3);
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(buf);
    } else {
      for (let i = 0; i < 3; i++) buf[i] = Math.floor(Math.random() * 4294967296);
    }
    let n = (buf[0] % 1000000) ^ (buf[1] % 1000000) ^ (buf[2] % 1000000);
    n = n % 1000000;
    return ('000000' + String(n)).slice(-6);
    } catch (e) {
    return '000000';
  }
};

// Device-only account used in local mode (legacy TVs / Clerk-less installs).
export const LOCAL_USER = {
  id: 'local',
  fullName: 'Guest Viewer',
  firstName: 'Guest',
  emailAddresses: [{ emailAddress: 'local@device' }],
  imageUrl: null,
  primaryEmailAddress: { emailAddress: 'local@device' },
};
